import { LinearGradient } from "@-/com.npmjs.expo-linear-gradient";
import { useCSSVariable, withUniwind } from "@-/com.npmjs.uniwind";
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { useEffect } from "react";

// An indeterminate, nprogress-style top loading bar — the Expo/React Native
// equivalent of `nextjs-toploader`. We never know the real reveal progress (it's
// a navigation that resolves whenever it resolves), so we *crawl*: fill fast at
// first, then ease out asymptotically toward ~90% and hold there while loading.
// When loading finishes we snap to 100% and fade out, exactly like nprogress.
// (The bar's `h-0.75` = 3px height matches nprogress' default 3px loading bar.)
const CRAWL_TARGET = 0.9;
// Initial jump so the bar is immediately visible (nprogress' `initialPosition`).
const INITIAL_PROGRESS = 0.08;
// Time to crawl from the initial position to the crawl target. Long + ease-out so
// the bar visibly decelerates and never quite reaches the end until `done` fires.
const CRAWL_DURATION_MS = 4000;
const COMPLETE_DURATION_MS = 200;
const FADE_DURATION_MS = 240;

const UniwindLinearGradient = withUniwind(LinearGradient);

// `useCSSVariable` returns either raw HSL channels ("221 83% 53%") on native or an
// already-resolved color string ("hsl(...)" / "#..." / "rgb(...)") on web, so only
// wrap when we got bare channels — otherwise we'd double-wrap to `hsl(hsl(...))`.
function hslFromChannels(channels: string | number | undefined): string {
  if (channels === undefined || channels === "") {
    return "rgba(34, 153, 221, 1)";
  }
  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }
  return `hsl(${value})`;
}

export function DrillPageRevealTopLoader({ active }: { active: boolean }) {
  // Resolve the secondary theme color at render time so the peg glow (a gradient
  // that needs an explicit color, unlike the `bg-secondary` className bar) matches
  // the bar in both light and dark mode.
  const secondaryColor = hslFromChannels(useCSSVariable("--color-secondary"));

  // 0 → 1 fraction of the bar's width that is filled.
  const progress = useSharedValue(0);
  // 1 while active / completing, 0 once faded out. Kept separate from `progress`
  // so we can fade the whole bar after the completion snap without collapsing the
  // fill back to 0 first.
  const opacity = useSharedValue(0);
  // Mounts the bar only while it has something to show. `0` = laid out as
  // `display: none` (can't intercept anything), `1` = present.
  const visible = useSharedValue(0);

  useEffect(() => {
    if (active) {
      cancelAnimation(progress);
      cancelAnimation(opacity);
      progress.set(INITIAL_PROGRESS);
      opacity.set(1);
      visible.set(1);
      // Ease-out crawl toward the hold point. `Easing.out(Easing.quad)` mimics
      // nprogress' decelerating trickle without per-frame random increments.
      progress.set(
        withTiming(CRAWL_TARGET, {
          duration: CRAWL_DURATION_MS,
          easing: Easing.out(Easing.quad),
        }),
      );
      return;
    }

    // Only run the completion animation if the bar was actually showing — avoids a
    // phantom flash on the initial mount, when `active` starts out false.
    if (visible.get() === 0) {
      return;
    }

    cancelAnimation(progress);
    // Snap to 100%, then fade the bar out and reset for the next reveal.
    progress.set(
      withTiming(1, {
        duration: COMPLETE_DURATION_MS,
        easing: Easing.out(Easing.cubic),
      }),
    );
    opacity.set(
      withSequence(
        withTiming(1, { duration: COMPLETE_DURATION_MS }),
        withTiming(0, { duration: FADE_DURATION_MS }, (finished) => {
          "worklet";
          if (finished) {
            progress.set(0);
            visible.set(0);
          }
        }),
      ),
    );
  }, [active, opacity, progress, visible]);

  useEffect(
    () => () => {
      cancelAnimation(progress);
      cancelAnimation(opacity);
    },
    [opacity, progress],
  );

  const containerStyle = useAnimatedStyle(() => ({
    // Skip layout entirely while idle so the bar can't intercept anything.
    display: visible.get() === 0 ? "none" : "flex",
    opacity: opacity.get(),
  }));

  const barStyle = useAnimatedStyle(() => ({
    // `scaleX` from a left anchor grows the bar from 0 → full width. (The bar uses
    // `origin-left` below; RN scales about the center otherwise.)
    transform: [{ scaleX: progress.get() }],
  }));

  return (
    <Animated.View
      className="absolute inset-x-0 top-0 z-30 h-0.75"
      pointerEvents="none"
      style={containerStyle}
    >
      <Animated.View className="size-full origin-left bg-secondary" style={barStyle}>
        {/* nprogress' "peg": a soft glow trailing the leading edge of the bar,
            faked with a horizontal gradient (RN/web both lack a cheap box-shadow
            blur here). Sits at the right edge of the (scaled) bar. */}
        <UniwindLinearGradient
          className="absolute inset-y-0 right-0 w-24"
          colors={["rgba(0, 0, 0, 0)", secondaryColor]}
          end={{ x: 1, y: 0.5 }}
          start={{ x: 0, y: 0.5 }}
        />
      </Animated.View>
    </Animated.View>
  );
}
